import { supabase } from "../config/supabaseClient";

/**
 * Apply company-specific is_critical for catalogue trainings.
 * - Company catalogue (training_catalogue_data.company_id set): is_critical from training_catalogue_data
 * - iFundi catalogue (company_id null): is_critical from training_catalogue_data_types
 *
 * @param {Array} trainings - Array of training_catalogue_data objects
 * @param {string} companyId - Company ID for iFundi catalogue queries
 * @returns {Promise<Array>} Trainings with is_critical updated where applicable
 */
export const checkTrainingsCriticalStatus = async (trainings, companyId) => {
  if (!trainings || trainings.length === 0) {
    return trainings;
  }

  // Separate company and iFundi trainings
  const companyTrainingIds = trainings
    .filter(t => t.company_id !== null && t.company_id !== undefined)
    .map(t => t.id || t.training_id)
    .filter(Boolean);
  
  const ifundiTrainingIds = trainings
    .filter(t => t.company_id === null || t.company_id === undefined)
    .map(t => t.id || t.training_id)
    .filter(Boolean);

  let updatedTrainings = [...trainings];

  // Check company catalogue trainings
  if (companyTrainingIds.length > 0) {
    const { data: companyData } = await supabase
      .from("training_catalogue_data")
      .select("id, is_critical")
      .in("id", companyTrainingIds);
    
    if (companyData) {
      const isCriticalMap = {};
      companyData.forEach(row => {
        isCriticalMap[row.id] = row.is_critical || false;
      });
      
      updatedTrainings = updatedTrainings.map(training => {
        const trainingId = training.id || training.training_id;
        if (isCriticalMap.hasOwnProperty(trainingId)) {
          return { ...training, is_critical: isCriticalMap[trainingId] };
        }
        return training;
      });
    }
  }

  // Check iFundi catalogue trainings — company-specific is_critical
  if (ifundiTrainingIds.length > 0) {
    const { data: ifundiData } = await supabase
      .from("training_catalogue_data_types")
      .select("training_id, is_critical")
      .in("training_id", ifundiTrainingIds)
      .eq("company_id", companyId);
    
    if (ifundiData) {
      const isCriticalMap = {};
      ifundiData.forEach(row => {
        isCriticalMap[row.training_id] = row.is_critical || false;
      });
      
      updatedTrainings = updatedTrainings.map(training => {
        const trainingId = training.id || training.training_id;
        if (isCriticalMap.hasOwnProperty(trainingId)) {
          return { ...training, is_critical: isCriticalMap[trainingId] };
        }
        return training;
      });
    }
  }

  return updatedTrainings;
};

/**
 * Save training assignments to critical skills - Update is_critical flag
 * @param {Array} selectedTrainingIds - Array of selected training IDs
 * @param {Array} relevantTrainings - Array of all relevant training objects
 * @param {string} companyId - Company ID
 * @param {Object} localUser - Current user object
 * @param {Function} updateTrainingInVectorDB - Function to update vector database
 * @returns {Promise<Object>} - Result object with success status and counts
 */
export const saveTrainingAssignments = async (
  selectedTrainingIds,
  relevantTrainings,
  companyId,
  localUser,
  updateTrainingInVectorDB
) => {
  const companyTrainings = [];
  const ifundiTrainings = [];
  const vectorUpdates = [];
  
  // Separate trainings by type (company vs iFundi)
  // Skip trainings that are already marked as critical
  for (const trainingId of selectedTrainingIds) {
    const training = relevantTrainings.find((t) => t.id === trainingId);
    
    if (!training) continue;
    
    // Skip if already critical
    if (training.is_critical === true) {
      continue;
    }

    const isCompanyTraining = training.company_id !== null && training.company_id !== undefined;
    
    if (isCompanyTraining) {
      // Company catalogue training - update training_catalogue_data
      companyTrainings.push({
        id: trainingId,
        training: training
      });
    } else {
      // iFundi catalogue training - update/create training_catalogue_data_types
      ifundiTrainings.push({
        id: trainingId,
        training: training
      });
    }
  }

  // Update company catalogue trainings
  if (companyTrainings.length > 0) {
    const companyTrainingIds = companyTrainings.map(t => t.id);
    
    const { error: companyError } = await supabase
      .from("training_catalogue_data")
      .update({ is_critical: true })
      .in("id", companyTrainingIds);

    if (companyError) {
      throw new Error(`Failed to update company trainings: ${companyError.message}`);
    }

    // Prepare vector database updates
    companyTrainings.forEach(({ id }) => {
      vectorUpdates.push({ trainingId: id, updates: { is_critical: true } });
    });
  }

  // Update/create iFundi catalogue trainings
  if (ifundiTrainings.length > 0) {
    const ifundiTrainingIds = ifundiTrainings.map(t => t.id);
    
    // Check which trainings already have entries
    const { data: existingEntries } = await supabase
      .from("training_catalogue_data_types")
      .select("training_id")
      .in("training_id", ifundiTrainingIds)
      .eq("company_id", companyId);

    const existingTrainingIds = new Set(
      (existingEntries || []).map(row => row.training_id)
    );

    // Separate into updates and inserts
    const toUpdate = ifundiTrainings.filter(t => existingTrainingIds.has(t.id));
    const toInsert = ifundiTrainings.filter(t => !existingTrainingIds.has(t.id));

    // Update existing entries
    if (toUpdate.length > 0) {
      const updateIds = toUpdate.map(t => t.id);
      const { error: updateError } = await supabase
        .from("training_catalogue_data_types")
        .update({ is_critical: true })
        .in("training_id", updateIds)
        .eq("company_id", companyId);

      if (updateError) {
        throw new Error(`Failed to update iFundi trainings: ${updateError.message}`);
      }
    }

    // Insert new entries
    if (toInsert.length > 0) {
      const insertData = toInsert.map(({ training }) => ({
        training_id: training.id || training.training_id,
        company_id: companyId,
        type: training.type || null,
        is_critical: true,
        // created_by: localUser?.id || localUser?.user_id || null,
      }));

      const { error: insertError } = await supabase
        .from("training_catalogue_data_types")
        .insert(insertData);

      if (insertError) {
        throw new Error(`Failed to create iFundi training entries: ${insertError.message}`);
      }
    }

    // Prepare vector database updates
    ifundiTrainings.forEach(({ id }) => {
      vectorUpdates.push({ trainingId: id, updates: { is_critical: true } });
    });
  }

  // Update vector database for all trainings (in parallel)
  if (vectorUpdates.length > 0) {
    await Promise.allSettled(
      vectorUpdates.map(({ trainingId, updates }) =>
        updateTrainingInVectorDB(trainingId, updates)
      )
    );
  }

  const totalUpdated = companyTrainings.length + ifundiTrainings.length;
  
  return {
    success: true,
    companyCount: companyTrainings.length,
    ifundiCount: ifundiTrainings.length,
    totalCount: totalUpdated
  };
};

